import _             from 'underscore';
import React, { FC } from 'react';

import { CorvalProjectActivity, FuncOrAsyncFunc, SystemEvent, Uuid } from '@src/types';

import { useFeatureFlags, useSystemModals, useSystemToasts }                                               from '@src/hooks';
import { ProjectActivityBulkUpdateHireType }                                                               from "@src/views/pages/projects/activities/bulk/slices/ProjectActivityBulkUpdateHireType";
import { ProjectActivityBulkUpdateDialogFormContainer, ProjectActivityBulkUpdateDialogFormContainerProps } from "@src/views/pages/projects/activities/bulk/ProjectActivityBulkUpdateDialogFormContainer";
import { UseIf }                                                                                           from "@src/views/components/display/EqualValues";
import { ProjectActivityBulkUpdateNotes }                                                                  from "@src/views/pages/projects/activities/bulk/slices/ProjectActivityBulkUpdateNotes";
import { runIfFunction }                                                                                   from "@src/util";
import { ProjectActivityBulkUpdateOperationalCost }                                                        from "@src/views/pages/projects/activities/bulk/slices/ProjectActivityBulkUpdateOperationalCost";
import { ProjectActivityBulkUpdateOngoingCost }                                                            from "@src/views/pages/projects/activities/bulk/slices/ProjectActivityBulkUpdateOngoingCost";
import { StaticProjectActivityBulkUpdateTiming }                                                           from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateTiming";
import { StaticProjectActivityBulkUpdateHireType }                                                         from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateHireType";
import { StaticProjectActivityBulkUpdateOperationalCost }                                                  from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateOperationalCost";
import { StaticProjectActivityBulkUpdateOngoingCost }                                                      from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateOngoingCost";
import { StaticProjectActivityBulkUpdateCriticality }                                                      from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateCriticality";
import { StaticProjectActivityBulkUpdateBusinessFunction }                                                 from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateBusinessFunction";
import { StaticProjectActivityBulkUpdateCompletionState }                                                  from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateCompletionState";
import { StaticProjectActivityBulkUpdateIsAtRisk }                                                         from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateIsAtRisk";


export type ProjectActivityBulkUpdateType =
    'timing' |
    'criticality' |
    'isAtRisk' |
    'completionState' |
    'businessFunction' |
    'hireType' |
    'notes' |
    'operationalCost' |
    'operationalCostOverride' |
    'ongoingCost' |
    'ongoingCostOverride';

export const projectActivityBulkUpdateTypes: Record<ProjectActivityBulkUpdateType, ProjectActivityBulkUpdateType> = {
    timing:                  'timing',
    criticality:             'criticality',
    isAtRisk:                'isAtRisk',
    completionState:         'completionState',
    businessFunction:        'businessFunction',
    operationalCost:         'operationalCost',
    operationalCostOverride: 'operationalCostOverride',
    ongoingCost:             'ongoingCost',
    ongoingCostOverride:     'ongoingCostOverride',
    hireType:                'hireType',
    notes:                   'notes',
}

export const corvalManagedProjectActivityBulkUpdateTypes: ProjectActivityBulkUpdateType[] = [
    projectActivityBulkUpdateTypes.operationalCostOverride,
    projectActivityBulkUpdateTypes.ongoingCostOverride,
    projectActivityBulkUpdateTypes.hireType,
    projectActivityBulkUpdateTypes.notes,
];

export const staticProjectActivityBulkUpdateTypes: ProjectActivityBulkUpdateType[] = [
    projectActivityBulkUpdateTypes.timing,
    projectActivityBulkUpdateTypes.criticality,
    projectActivityBulkUpdateTypes.isAtRisk,
    projectActivityBulkUpdateTypes.completionState,
    projectActivityBulkUpdateTypes.businessFunction,
    projectActivityBulkUpdateTypes.operationalCost,
    projectActivityBulkUpdateTypes.ongoingCost,
    projectActivityBulkUpdateTypes.hireType,
    projectActivityBulkUpdateTypes.notes,
];

export const projectActivityBulkUpdateTypeTitles: Record<ProjectActivityBulkUpdateType, string> = {
    timing:                  'Timing',
    criticality:             'Priority',
    isAtRisk:                'At Risk',
    completionState:         'Status',
    businessFunction:        'Business Function',
    operationalCost:         'Operational Cost',
    operationalCostOverride: 'Operational Cost',
    ongoingCost:             'Ongoing Cost',
    ongoingCostOverride:     'Ongoing Cost',
    hireType:                'Hire Types',
    notes:                   'Notes',
}

export interface ProjectActivityBulkUpdateDialogProps {
    projectId: Uuid,
    projectVersionId: Uuid | null,
    projectActivityIds: Uuid[],
    updateType: ProjectActivityBulkUpdateType,
    title?: string,
    closeOnEntityUpdate?: boolean,
    onUpdateComplete: FuncOrAsyncFunc<[ mutatedActivitySet: Array<CorvalProjectActivity> ], void>,
    onUpdateError: FuncOrAsyncFunc<[ e: Error ], void>,

    onUpdateCanceled?( e: SystemEvent ): void,
}

const defaultProps: ProjectActivityBulkUpdateDialogProps = {
    title:               null,
    updateType:          null,
    projectId:           null,
    projectVersionId:    null,
    projectActivityIds:  [],
    onUpdateComplete:    null,
    onUpdateError:       null,
    closeOnEntityUpdate: false,
}

const ProjectActivityBulkUpdateDialog: FC<ProjectActivityBulkUpdateDialogProps> = ( props ) => {

    let { projectId, projectVersionId, projectActivityIds } = props,
        toastActions                                        = useSystemToasts(),
        modalActions                                        = useSystemModals(),
        featureFlags                                        = useFeatureFlags();

    const permissionHash: Map<ProjectActivityBulkUpdateType, boolean> = new Map( [
        [ projectActivityBulkUpdateTypes.timing, true ],
        [ projectActivityBulkUpdateTypes.criticality, true ],
        [ projectActivityBulkUpdateTypes.isAtRisk, true ],
        [ projectActivityBulkUpdateTypes.completionState, true ],
        [ projectActivityBulkUpdateTypes.businessFunction, true ],
        [ projectActivityBulkUpdateTypes.operationalCost, featureFlags.permissionProjectActivityUpdateOperationalCost ],
        [ projectActivityBulkUpdateTypes.operationalCostOverride, featureFlags.permissionProjectActivityUpdateOperationalCost ],
        [ projectActivityBulkUpdateTypes.ongoingCost, featureFlags.permissionProjectActivityUpdateOngoingCost ],
        [ projectActivityBulkUpdateTypes.ongoingCostOverride, featureFlags.permissionProjectActivityUpdateOngoingCost ],
        [ projectActivityBulkUpdateTypes.hireType, featureFlags.permissionProjectActivityUpdateHireTypes ],
        [ projectActivityBulkUpdateTypes.notes, true ],
    ] );

    const dialogTitle   = props.title ?? `Bulk Update ${ projectActivityBulkUpdateTypeTitles[ props.updateType ] }`,
          userCanUpdate = permissionHash.get( props.updateType );

    // Indicate if we're using the Corval managed areas
    // for (Project Activities), or not (Static Project Activities)
    const displayCorvalManagedControls = _.isEmpty( projectVersionId );

    const bulkUpdaterProps = {
        projectId,
        projectVersionId,
        projectActivityIds,
        supportModals: true,
        supportToasts: true,
        formContainer: ( formContainerProps: ProjectActivityBulkUpdateDialogFormContainerProps ) => (
            <ProjectActivityBulkUpdateDialogFormContainer

                { ...formContainerProps }

                title={ dialogTitle }
                operationIsHidden={ !userCanUpdate }
                onComplete={ ( data ) => {
                    formContainerProps.onComplete( data );
                    runIfFunction( props.onUpdateComplete, data.updateProjectActivities );
                } }
                onError={ ( data ) => {
                    formContainerProps.onError( data );
                    runIfFunction( props.onUpdateError, data );
                } }
                closeOnEntityUpdate={ props.closeOnEntityUpdate }
            />
        )
    }

    return (

        <>
            {/* General */ }
            <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.notes }>
                <ProjectActivityBulkUpdateNotes { ...bulkUpdaterProps } />
            </UseIf>

            {/* Static Project Activities */ }
            <UseIf x={ !displayCorvalManagedControls }>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.hireType }>
                    <StaticProjectActivityBulkUpdateHireType { ...bulkUpdaterProps } />
                </UseIf>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.timing }>
                    <StaticProjectActivityBulkUpdateTiming { ...bulkUpdaterProps } />
                </UseIf>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.operationalCost }>
                    <StaticProjectActivityBulkUpdateOperationalCost { ...bulkUpdaterProps } />
                </UseIf>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.ongoingCost }>
                    <StaticProjectActivityBulkUpdateOngoingCost { ...bulkUpdaterProps } />
                </UseIf>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.criticality }>
                    <StaticProjectActivityBulkUpdateCriticality { ...bulkUpdaterProps } />
                </UseIf>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.businessFunction }>
                    <StaticProjectActivityBulkUpdateBusinessFunction { ...bulkUpdaterProps } />
                </UseIf>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.completionState }>
                    <StaticProjectActivityBulkUpdateCompletionState { ...bulkUpdaterProps } />
                </UseIf>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.isAtRisk }>
                    <StaticProjectActivityBulkUpdateIsAtRisk { ...bulkUpdaterProps } />
                </UseIf>

            </UseIf>

            {/* Project Activities */ }
            <UseIf x={ displayCorvalManagedControls }>

                <UseIf x={ !projectVersionId && ( props.updateType === projectActivityBulkUpdateTypes.hireType ) }>
                    <ProjectActivityBulkUpdateHireType { ...bulkUpdaterProps } />
                </UseIf>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.operationalCostOverride }>
                    <ProjectActivityBulkUpdateOperationalCost { ...bulkUpdaterProps } />
                </UseIf>

                <UseIf x={ props.updateType === projectActivityBulkUpdateTypes.ongoingCostOverride }>
                    <ProjectActivityBulkUpdateOngoingCost { ...bulkUpdaterProps } />
                </UseIf>

            </UseIf>
        </>
    )
};

ProjectActivityBulkUpdateDialog.defaultProps = defaultProps;

export { ProjectActivityBulkUpdateDialog }
